import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import uuidv4 from 'uuid/v4'
import { getSummitMode } from '../../VNMode/Selectors'
import PeripheralBridge from '../../utils/peripheralBridge'
import { isRunningOnAndroid } from '../../VNAndroidSDK/Utils'
import createDataDogLog from '../../utils/datadog'
import { ToastManager } from '../../utils/toastManager'
import callable from '../../utils/callable'
import { getDeviceId, getDeviceSerial } from '../../utils/deviceData'

const RETRY_TIMEOUT = 2_000
const RETRY_ATTEMPTS_LIMIT = 5

/**
 * Custom hook to manage device identifiers based on summit mode
 * Backwards compatible with non-summit devices using deviceId
 */
const useDeviceIdentifiers = () => {
  const isSummit = useSelector(getSummitMode)

  /**
   * Initialize device ID or serial number based on summit mode
   * Retries fetching from the bridge if not immediately available
   * Falls back to random UUIDs for non-APK environments (e.g. browser)
   * @param {number} retryCount - Current retry attempt count
   */
  const initializeDeviceId = useCallback(
    (callback, retryCount = 0) => {
      if (retryCount >= RETRY_ATTEMPTS_LIMIT) {
        createDataDogLog('error', 'Failed to initialize device ID after multiple attempts')
        ToastManager.error('Failed to initialize device ID. Please restart the app.', { autoClose: false })
        return
      }
      const bridgeInstance = PeripheralBridge.getBridge()
      const isApk = isRunningOnAndroid()

      // Non-APK (e.g. browser) fallback to random UUIDs
      if (!isApk) {
        const localDeviceId = uuidv4()
        window.localStorage.setItem('device_id', localDeviceId)

        if (isSummit) {
          const localSerialNumber = uuidv4()
          window.localStorage.setItem('serial_number', localSerialNumber)
          callable(callback, localSerialNumber)
          return
        }

        callable(callback, localDeviceId)
        return
      }

      // Retry if bridge is not yet available
      if (!bridgeInstance) {
        setTimeout(() => initializeDeviceId(callback, retryCount + 1), RETRY_TIMEOUT)
        return
      }

      // Pre-Summit mode uses device_id only
      if (!isSummit) {
        bridgeInstance.callHandler('getSerialNumber', null, (uuid) => {
          if (!uuid) {
            setTimeout(() => initializeDeviceId(callback, retryCount + 1), RETRY_TIMEOUT)
            return
          }
          window.localStorage.setItem('device_id', uuid)
          callable(callback, uuid)
        })
        return
      }

      // Summit mode uses serial_number
      bridgeInstance.callHandler('getDeviceIdentifiers', null, (data) => {
        const { deviceId, serialNumber } = data
        // if we are on E700 mayube A930, the serial number is '' and we should use deviceId instead
        if (!deviceId) {
          setTimeout(() => initializeDeviceId(callback, retryCount + 1), RETRY_TIMEOUT)
          return
        }

        window.localStorage.setItem('device_id', deviceId)
        // older devices may not have a serial number
        if (serialNumber) {
          window.localStorage.setItem('serial_number', serialNumber)
          callable(callback, serialNumber)
          return
        }
        callable(callback, deviceId)
      })
      return
    },
    [isSummit],
  )

  /**
   * Returns empty if in summit and no serial number is set
   * Defaults to device_id for non-summit devices
   * @returns {string|null} - device_id for non-summit, serial_number for summit, or null if not set
   */
  const getConditionalDeviceId = useCallback(() => {
    if (!isSummit) {
      return getDeviceId()
    }
    return getDeviceSerial(false)
  }, [isSummit])

  /**
   * Get the current device identifiers
   * @returns {object} - Object containing deviceId and serialNumber
   */
  const getIdentifiers = useCallback(() => {
    return {
      deviceId: getDeviceId(),
      serialNumber: getDeviceSerial(false) || undefined,
      isSummit,
    }
  }, [isSummit])

  /**
   * Set the device identifier based on summit mode
   * @param {string} id - The device ID or serial number to set
   */
  const setDeviceId = useCallback(
    (id) => {
      if (!isSummit) {
        window.localStorage.setItem('device_id', id)
        return
      }
      window.localStorage.setItem('serial_number', id)
    },
    [isSummit],
  )

  const getRegistrationUrl = useCallback(() => {
    const issuer = process.env.REACT_APP_OIDC_ISSUER
    const orgName = process.env.REACT_APP_ORG
    const appType = 'pos'
    const baseUrl = `${issuer}/devices/register/${appType}`
    const deviceId = getDeviceId()
    const serialNumber = getDeviceSerial(false)

    if (!isSummit) {
      if (!deviceId) return null
      return `${baseUrl}/${deviceId}?organization_name=${orgName}`
    }

    if (!serialNumber) return null
    return `${baseUrl}?serial_number=${serialNumber}&organization_name=${orgName}`
  }, [isSummit])

  return {
    getConditionalDeviceId,
    getIdentifiers,
    getRegistrationUrl,
    initializeDeviceId,
    isSummit,
    setDeviceId,
  }
}

export default useDeviceIdentifiers
